import { useRef, useState } from 'react';
import { usePatientsShortQuery } from '@common/cache';
import { debounce } from '@util/debounce.ts';
import { useNavigate } from 'react-router-dom';

interface PatientDetails {
  id: string;
  name: string;
}

export const usePatientSearchBar = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [search, setSearch] = useState('');
  const [value, setValue] = useState<PatientDetails | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [placeholder, setPlaceholder] = useState('Search patient');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data = [], isLoading } = usePatientsShortQuery({ search: search });

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setIsTyping(true);

    debounce(
      () => {
        setSearch(value);
        setIsTyping(false);
      },
      true,
      300,
    );
  };

  const handleValueChange = (value: PatientDetails | null) => {
    setValue(value);
    setSearchQuery(value?.name || '');
    setSearch(value?.name || '');
  };

  const onBlur = () => {
    setPlaceholder('Search patient');
  };

  const onFocus = () => {
    setPlaceholder(
      'Enter patient name, code, MBI, date of birth, phone number, SF ID or Brigade EMR ID',
    );
  };

  const onClear = () => {
    setSearchQuery('');
    setSearch('');
  };

  const handleNavigate = (value: PatientDetails) => {
    setValue(null);
    setSearchQuery('');
    setSearch('');
    navigate(`/patient/${value.id}`);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  return {
    searchQuery,
    value,
    handleSearchChange,
    handleValueChange,
    onFocus,
    onBlur,
    placeholder,
    patientOptions: data,
    isLoading: isLoading || isTyping,
    handleNavigate,
    inputRef,
    onClear,
  };
};
